import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { involvementAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
    Gauge, AlertTriangle, Sparkles, ArrowUp, ArrowDown, Trophy, TrendingDown, UsersRound, CalendarRange,
    Crown, Users, Loader2, ThumbsUp, ThumbsDown, Target, MessageSquare, Clock,
    Briefcase, GraduationCap, MessageCircle, Megaphone, Activity, ListTodo, Search,
    FileSpreadsheet, FileText, Timer, Repeat, LogIn,
} from 'lucide-react';
import { toast } from 'sonner';
import OneToOneBriefButton from '@/components/dashboard/OneToOneBriefButton';

// Définition des 6 sous-scores affichés
const SUBS = [
    { key: 'crm', label: 'CRM', icon: Briefcase },
    { key: 'discipline', label: 'Discipline', icon: ListTodo },
    { key: 'reactivite', label: 'Réactivité', icon: MessageCircle },
    { key: 'academie', label: 'Académie', icon: GraduationCap },
    { key: 'inside_vnx', label: 'Inside VNX', icon: Activity },
    { key: 'animation', label: 'Animation', icon: Megaphone },
];

// L'Animation ne concerne QUE les admins et responsables de secteur (VP). Pour un simple
// apporteur, elle est exclue de l'affichage (et déjà exclue de la note globale côté backend).
const isVPRole = (role) => role === 'vp' || role === 'admin' || role === 'admin_full';
const subsForRole = (role) => isVPRole(role) ? SUBS : SUBS.filter(s => s.key !== 'animation');

const scoreColor = (s) => {
    if (s === null || s === undefined) return 'text-slate-400';
    if (s >= 85) return 'text-emerald-600';
    if (s >= 70) return 'text-green-600';
    if (s >= 50) return 'text-amber-600';
    if (s >= 30) return 'text-orange-600';
    return 'text-red-600';
};
const barColor = (s) => {
    if (s >= 85) return 'bg-emerald-500';
    if (s >= 70) return 'bg-green-500';
    if (s >= 50) return 'bg-amber-500';
    if (s >= 30) return 'bg-orange-500';
    return 'bg-red-500';
};
const labelBadge = (s) => {
    if (s === null || s === undefined) return 'bg-slate-100 text-slate-500';
    if (s >= 85) return 'bg-emerald-100 text-emerald-700';
    if (s >= 70) return 'bg-green-100 text-green-700';
    if (s >= 50) return 'bg-amber-100 text-amber-700';
    if (s >= 30) return 'bg-orange-100 text-orange-700';
    return 'bg-red-100 text-red-700';
};
const riskBadge = (r) => ({
    'Faible': 'bg-emerald-100 text-emerald-700',
    'Moyen': 'bg-amber-100 text-amber-700',
    'Élevé': 'bg-orange-100 text-orange-700',
    'Critique': 'bg-red-100 text-red-700',
}[r] || 'bg-slate-100 text-slate-500');

// Date de dernière connexion en relatif + date courte (fr)
const relDate = (iso, days) => {
    if (!iso || days === null || days === undefined) return 'Jamais';
    const d = new Date(iso);
    const dateStr = isNaN(d) ? '' : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    if (days <= 0) return "Aujourd'hui";
    if (days === 1) return dateStr ? `Hier (${dateStr})` : 'Hier';
    return dateStr ? `il y a ${days} j (${dateStr})` : `il y a ${days} j`;
};
// Couleur selon l'ancienneté de la connexion CRM (vert récent → rouge ancien)
const loginColor = (days) => {
    if (days === null || days === undefined) return 'text-slate-400';
    if (days <= 1) return 'text-emerald-600';
    if (days <= 3) return 'text-green-600';
    if (days <= 7) return 'text-amber-600';
    if (days <= 10) return 'text-orange-600';
    return 'text-red-600';
};

// Bloc compact des infos de connexion (carte + détail)
function ConnInfo({ metrics }) {
    const items = [
        { icon: LogIn, label: 'CRM', value: relDate(metrics.last_crm_login, metrics.jours_depuis_connexion_crm),
          color: loginColor(metrics.jours_depuis_connexion_crm) },
        { icon: Activity, label: 'Inside VNX', value: relDate(metrics.last_vnx_login, metrics.jours_depuis_connexion_vnx),
          color: loginColor(metrics.jours_depuis_connexion_vnx) },
        { icon: Repeat, label: 'Fréquence',
          value: metrics.frequence_moyenne_jours ? `tous les ${metrics.frequence_moyenne_jours} j` : 'N/A', color: 'text-slate-700' },
        { icon: Timer, label: 'Session moy.',
          value: metrics.temps_session_moyen_minutes != null ? `${metrics.temps_session_moyen_minutes} min` : 'N/A', color: 'text-slate-700' },
    ];
    return (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-lg bg-slate-50 p-2.5" data-testid="conn-info">
            {items.map((it) => (
                <div key={it.label} className="flex items-center gap-1.5 text-[11px]" data-testid={`conn-${it.label}`}>
                    <it.icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="text-slate-500 shrink-0">{it.label} :</span>
                    <span className={`font-semibold truncate ${it.color}`}>{it.value}</span>
                </div>
            ))}
        </div>
    );
}

function ScoreRing({ value, size = 64 }) {
    const r = (size - 8) / 2;
    const c = 2 * Math.PI * r;
    const pct = value === null || value === undefined ? 0 : value;
    const off = c - (pct / 100) * c;
    const stroke = pct >= 70 ? '#16a34a' : pct >= 50 ? '#d97706' : pct >= 30 ? '#ea580c' : '#dc2626';
    return (
        <svg width={size} height={size} className="shrink-0">
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth="6" />
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={value === null ? '#cbd5e1' : stroke}
                strokeWidth="6" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
                transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dashoffset .6s ease' }} />
            <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle"
                className={`text-sm font-bold ${scoreColor(value)}`} fill="currentColor">
                {value === null || value === undefined ? '—' : Math.round(value)}
            </text>
        </svg>
    );
}

function SubScore({ icon: Icon, label, value }) {
    return (
        <div className="space-y-1" data-testid={`subscore-${label}`}>
            <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1 text-slate-600"><Icon className="w-3.5 h-3.5" />{label}</span>
                <span className={`font-semibold ${scoreColor(value)}`}>{value === null || value === undefined ? 'N/A' : Math.round(value)}</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div className={`h-full rounded-full ${barColor(value || 0)}`} style={{ width: `${value || 0}%`, transition: 'width .5s' }} />
            </div>
        </div>
    );
}

export default function Implication() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // Redirections cohérentes vers les listes filtrées (respectent la visibilité via les pages cibles)
    const goProspects = (uid, extra = '') => navigate(`/prospects?user=${uid}${extra}`);
    const goProspectIds = (ids) => navigate(`/prospects?ids=${(ids || []).join(',')}&label=${encodeURIComponent('À relancer')}`);
    const goTasks = (uid, status) => navigate(`/tasks?member=${uid}${status ? `&status=${status}` : ''}`);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [history, setHistory] = useState(null);
    const [recap, setRecap] = useState(null);
    const [windowDays, setWindowDays] = useState(30);
    const [roleFilter, setRoleFilter] = useState('all');
    const [teamFilter, setTeamFilter] = useState('all');
    const [search, setSearch] = useState('');

    const load = useCallback(async (wd) => {
        setLoading(true);
        try {
            const [r, rec] = await Promise.all([
                involvementAPI.scores(wd),
                involvementAPI.weeklyRecap(wd).catch(() => ({ data: null })),
            ]);
            setData(r.data);
            setRecap(rec.data);
        } catch (e) {
            toast.error("Impossible de charger l'Impact Score");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(windowDays); }, [load, windowDays]);

    // Deep-link Uatu : `?user=<id>` — ouvre AUTOMATIQUEMENT la modale détail
    // du membre (avec sous-scores dépliés). Brief fév. 2026.
    useEffect(() => {
        const uid = searchParams.get('user');
        if (!uid) return;
        if (loading || !data) return;
        // Vérifie que le membre existe dans le dataset chargé (RBAC)
        const found = (data?.scores || []).some((r) => r.user_id === uid);
        if (!found) return;
        openUser(uid);
        // Nettoie l'URL après ouverture pour éviter la réouverture au refresh
        try {
            const next = new URLSearchParams(searchParams);
            next.delete('user');
            setSearchParams(next, { replace: true });
        } catch { /* noop */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, loading, data]);

    const openUser = async (userId) => {
        setSelected(userId); setDetail(null); setHistory(null);
        try {
            const [d, h] = await Promise.all([
                involvementAPI.userDetail(userId, windowDays),
                involvementAPI.history(userId, 90),
            ]);
            setDetail(d.data);
            setHistory(h.data.history || []);
        } catch { toast.error('Erreur de chargement du détail'); }
    };

    const runAi = async () => {
        if (!selected) return;
        setAiLoading(true);
        try {
            const r = await involvementAPI.aiAnalysis(selected, windowDays);
            setDetail((d) => ({ ...d, ai_analysis: r.data }));
            if (r.data.ai_available === false) toast.error('IA momentanément indisponible');
            else toast.success('Analyse IA générée');
        } catch { toast.error("Échec de l'analyse IA"); }
        finally { setAiLoading(false); }
    };

    const exportRecap = async (format) => {
        try {
            const r = await involvementAPI.exportRecap(format, windowDays, teamFilter);
            const teamName = teamFilter !== 'all'
                ? (data?.teams?.find(t => t.id === teamFilter)?.name || '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '')
                : '';
            const slug = teamName ? `${teamName}_` : '';
            const url = window.URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `impact_score_${slug}${new Date().toISOString().slice(0, 10)}.${format}`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch { toast.error("Échec de l'export"); }
    };

    const allScored = useMemo(() => (data?.scores || []).filter(s => s.global_score !== null), [data]);
    const scores = useMemo(() => {
        let list = allScored;
        if (roleFilter !== 'all') {
            list = list.filter(s => roleFilter === 'admin'
                ? (s.role === 'admin' || s.role === 'admin_full') : s.role === roleFilter);
        }
        if (teamFilter !== 'all') {
            list = list.filter(s => (s.teams || []).some(t => t.id === teamFilter));
        }
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(s => `${s.first_name} ${s.last_name}`.toLowerCase().includes(q));
        }
        return list;
    }, [allScored, roleFilter, teamFilter, search]);

    const top3 = useMemo(() => [...scores].slice(0, 3), [scores]);
    const bottom3 = useMemo(() => [...scores].slice(-3).reverse(), [scores]);
    const withProg = useMemo(() => scores.filter(s => s.progression !== null && s.progression !== undefined), [scores]);
    const topProg = useMemo(() => [...withProg].sort((a, b) => b.progression - a.progression).slice(0, 3), [withProg]);
    const topReg = useMemo(() => [...withProg].sort((a, b) => a.progression - b.progression).slice(0, 3).filter(s => s.progression < 0), [withProg]);

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="implication-page">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
                    <div>
                        <h1 className="text-3xl font-bold flex items-center gap-2">
                            <Gauge className="w-7 h-7 text-blue-600" /> Impact Score
                        </h1>
                        <p className="text-slate-500 mt-1">
                            Implication globale par utilisateur : CRM, Discipline, Réactivité, Académie, Inside VNX, Animation.
                            Photo instantanée · activité sur 30 jours · dossiers gagnés/perdus exclus du suivi.
                        </p>
                    </div>
                    {data && (
                        <Card className="px-5 py-3" data-testid="team-average-card">
                            <div className="flex items-center gap-3">
                                <ScoreRing value={data.team_average} size={56} />
                                <div>
                                    <p className="text-xs text-slate-500">Moyenne équipe</p>
                                    <p className="text-xs text-slate-400">{data.count} évalué(s)</p>
                                </div>
                            </div>
                        </Card>
                    )}
                </div>

                {/* Récap hebdomadaire */}
                {recap && recap.count > 0 && (
                    <Card className="border-blue-100 bg-gradient-to-br from-blue-50/60 to-indigo-50/40" data-testid="weekly-recap">
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between gap-2">
                                <CardTitle className="text-base flex items-center gap-2 text-blue-800">
                                    <CalendarRange className="w-5 h-5" /> Récap hebdomadaire
                                </CardTitle>
                                <div className="flex gap-2">
                                    <Button size="sm" variant="outline" onClick={() => exportRecap('csv')} data-testid="export-csv-btn">
                                        <FileSpreadsheet className="w-4 h-4 mr-1.5" />CSV
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => exportRecap('pdf')} data-testid="export-pdf-btn">
                                        <FileText className="w-4 h-4 mr-1.5" />PDF
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-4 md:grid-cols-4">
                                <div className="rounded-lg bg-white/70 p-3">
                                    <p className="text-xs text-slate-500">Moyenne équipe</p>
                                    <p className="text-2xl font-bold text-slate-800">{recap.team_average}
                                        {recap.delta !== null && recap.delta !== undefined && (
                                            <span className={`ml-2 text-sm font-semibold ${recap.delta >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                                                {recap.delta >= 0 ? '+' : ''}{recap.delta} vs S-1
                                            </span>
                                        )}
                                    </p>
                                    {recap.delta === null && <p className="text-[11px] text-slate-400">Comparaison dispo. dès la semaine prochaine</p>}
                                </div>
                                <div className="rounded-lg bg-white/70 p-3">
                                    <p className="text-xs text-slate-500 mb-1">Répartition décrochage</p>
                                    <div className="flex flex-wrap gap-1">
                                        {['Faible', 'Moyen', 'Élevé', 'Critique'].map(k => (
                                            (recap.risque_counts?.[k] || 0) > 0 &&
                                            <Badge key={k} className={riskBadge(k)}>{k} : {recap.risque_counts[k]}</Badge>
                                        ))}
                                    </div>
                                </div>
                                <div className="rounded-lg bg-white/70 p-3">
                                    <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><ArrowUp className="w-3.5 h-3.5 text-emerald-600" />Top progression</p>
                                    {recap.top_progression.length === 0 ? <p className="text-[11px] text-slate-400">En constitution</p> :
                                        recap.top_progression.map(u => (
                                            <button key={u.user_id} onClick={() => openUser(u.user_id)} className="block text-sm text-slate-700 hover:underline">
                                                {u.first_name} {u.last_name} <span className="text-emerald-600 font-semibold">+{u.progression}</span>
                                            </button>
                                        ))}
                                </div>
                                <div className="rounded-lg bg-white/70 p-3">
                                    <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><TrendingDown className="w-3.5 h-3.5 text-red-600" />À accompagner</p>
                                    {recap.to_accompany.map(u => (
                                        <button key={u.user_id} onClick={() => openUser(u.user_id)} className="block text-sm text-slate-700 hover:underline">
                                            {u.first_name} {u.last_name} <span className={`font-semibold ${scoreColor(u.global_score)}`}>{Math.round(u.global_score)}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {recap.team_average_history && recap.team_average_history.length > 1 && (
                                <div className="mt-4 rounded-lg bg-white/70 p-3" data-testid="team-avg-chart">
                                    <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                                        <Activity className="w-3.5 h-3.5 text-blue-600" />Évolution de la moyenne d'équipe
                                    </p>
                                    <ResponsiveContainer width="100%" height={140}>
                                        <LineChart data={recap.team_average_history.map(h => ({ date: h.date?.slice(5), moyenne: h.avg }))}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                                            <XAxis dataKey="date" fontSize={11} stroke="#94a3b8" />
                                            <YAxis domain={[0, 100]} fontSize={11} stroke="#94a3b8" width={28} />
                                            <Tooltip />
                                            <Line type="monotone" dataKey="moyenne" stroke="#4f46e5" strokeWidth={2} dot={{ r: 2 }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Comparatif des équipes */}
                {!loading && (data?.teams_summary?.length || 0) > 0 && (
                    <Card data-testid="teams-comparison">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                                <UsersRound className="w-5 h-5 text-indigo-600" /> Comparatif des équipes
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-1.5">
                            {data.teams_summary.map((t, i) => (
                                <button key={t.id} onClick={() => setTeamFilter(t.id)}
                                    className={`flex w-full flex-col gap-2 rounded-lg border px-3 py-2.5 text-left transition hover:bg-slate-50 sm:flex-row sm:items-center sm:gap-3 sm:py-2 ${teamFilter === t.id ? 'border-indigo-300 bg-indigo-50/50' : ''}`}
                                    data-testid={`team-row-${t.id}`}>
                                    {/* Ligne 1 : rang + nom + (score sur mobile) */}
                                    <div className="flex items-center gap-3 min-w-0 sm:flex-1">
                                        <span className="w-5 shrink-0 text-sm font-semibold text-slate-400">{i + 1}</span>
                                        <span className="flex-1 min-w-0 font-medium text-slate-700 break-words sm:truncate">{t.name}</span>
                                        <span className={`shrink-0 text-lg font-bold sm:hidden ${scoreColor(t.average)}`}>{t.average ?? '—'}</span>
                                    </div>
                                    {/* Ligne 2 (mobile) / bloc droit (desktop) : effectif + badges + delta + barre */}
                                    <div className="flex items-center gap-2 pl-8 sm:gap-3 sm:pl-0">
                                        <span className="text-xs text-slate-400 whitespace-nowrap">{t.count} membre{t.count > 1 ? 's' : ''}</span>
                                        {(t.risque_counts?.['Critique'] || 0) > 0 && (
                                            <Badge className="bg-red-100 text-red-700 text-[11px] whitespace-nowrap">{t.risque_counts['Critique']} critique</Badge>
                                        )}
                                        {t.delta !== null && t.delta !== undefined && (
                                            <span className={`flex items-center text-xs font-semibold ${t.delta >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                                                {t.delta >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}{Math.abs(t.delta)}
                                            </span>
                                        )}
                                        <div className="ml-auto w-24 sm:ml-0 sm:w-28">
                                            <div className="hidden items-center justify-end gap-2 sm:flex">
                                                <span className={`font-bold ${scoreColor(t.average)}`}>{t.average ?? '—'}</span>
                                            </div>
                                            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                                                <div className={`h-full rounded-full ${barColor(t.average || 0)}`} style={{ width: `${t.average || 0}%` }} />
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            ))}
                            {teamFilter !== 'all' && (
                                <button onClick={() => setTeamFilter('all')} className="text-xs text-blue-600 hover:underline pt-1" data-testid="team-clear">
                                    Réinitialiser le filtre équipe
                                </button>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Filtres */}
                <div className="flex flex-wrap items-center gap-2" data-testid="impact-filters">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input value={search} onChange={e => setSearch(e.target.value)}
                            placeholder="Rechercher un utilisateur" className="pl-8 w-56" data-testid="impact-search" />
                    </div>
                    <Select value={roleFilter} onValueChange={setRoleFilter}>
                        <SelectTrigger className="w-40" data-testid="impact-role-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous les rôles</SelectItem>
                            <SelectItem value="vp">VP</SelectItem>
                            <SelectItem value="apporteur">Apporteurs</SelectItem>
                            <SelectItem value="admin">Admins</SelectItem>
                        </SelectContent>
                    </Select>
                    {(data?.teams?.length || 0) > 1 && (
                        <Select value={teamFilter} onValueChange={setTeamFilter}>
                            <SelectTrigger className="w-48" data-testid="impact-team-filter"><SelectValue placeholder="Équipe" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Toutes les équipes</SelectItem>
                                {data.teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    )}
                    <Select value={String(windowDays)} onValueChange={v => setWindowDays(Number(v))}>
                        <SelectTrigger className="w-36" data-testid="impact-period-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="7">7 jours</SelectItem>
                            <SelectItem value="30">30 jours</SelectItem>
                            <SelectItem value="90">90 jours</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Classements */}
                {!loading && scores.length > 0 && (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="rankings">
                        <RankCard title="Top implication" icon={Trophy} color="text-emerald-600" items={top3} onClick={openUser} />
                        <RankCard title="À accompagner" icon={TrendingDown} color="text-red-600" items={bottom3} onClick={openUser} />
                        <RankCard title="Top progression" icon={ArrowUp} color="text-blue-600" items={topProg} onClick={openUser} showProg empty="Historique en constitution" />
                        <RankCard title="Top régression" icon={ArrowDown} color="text-orange-600" items={topReg} onClick={openUser} showProg empty="Aucune régression" />
                    </div>
                )}

                {loading ? (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-56 rounded-xl" />)}
                    </div>
                ) : scores.length === 0 ? (
                    <Card><CardContent className="py-10 text-center text-slate-500">
                        Aucun utilisateur à évaluer pour le moment.
                    </CardContent></Card>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="scores-grid">
                        {scores.map((u) => (
                            <Card key={u.user_id} className="cursor-pointer transition hover:shadow-md hover:-translate-y-0.5"
                                onClick={() => openUser(u.user_id)} data-testid={`score-card-${u.user_id}`}>
                                <CardHeader className="pb-3">
                                    <div className="flex items-center gap-3">
                                        <ScoreRing value={u.global_score} />
                                        <div className="min-w-0 flex-1">
                                            <CardTitle className="text-base truncate flex items-center gap-1.5">
                                                {u.role === 'vp' ? <Crown className="w-4 h-4 text-purple-600 shrink-0" /> : <Users className="w-4 h-4 text-green-600 shrink-0" />}
                                                {u.first_name} {u.last_name}
                                            </CardTitle>
                                            <div className="mt-1 flex flex-wrap gap-1">
                                                <Badge className={labelBadge(u.global_score)}>{u.label}</Badge>
                                                <Badge className={riskBadge(u.risque_decrochage)} data-testid={`risk-${u.user_id}`}>
                                                    Décrochage : {u.risque_decrochage}
                                                </Badge>
                                                {u.progression !== null && u.progression !== undefined && u.progression !== 0 && (
                                                    <Badge variant="outline" className={u.progression > 0 ? 'text-emerald-700 border-emerald-200' : 'text-orange-700 border-orange-200'}>
                                                        {u.progression > 0 ? '+' : ''}{u.progression}
                                                    </Badge>
                                                )}
                                                {(u.role === 'vp' || u.role === 'admin' || u.role === 'admin_full') && u.team_impact_score !== null && u.team_impact_score !== undefined && (
                                                    <Badge className="bg-indigo-100 text-indigo-700 gap-1" data-testid={`team-impact-${u.user_id}`}>
                                                        <UsersRound className="w-3 h-3" />Équipe {u.team_impact_score}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                        {subsForRole(u.role).map(s => <SubScore key={s.key} icon={s.icon} label={s.label} value={u.scores[s.key]} />)}
                                    </div>
                                    <ConnInfo metrics={u.metrics} />
                                    <div className="flex flex-wrap gap-1.5 pt-1 text-[11px]">
                                        <Badge variant="secondary" role="button" data-testid={`score-actifs-${u.user_id}`}
                                            onClick={(e) => { e.stopPropagation(); goProspects(u.user_id, '&view=en_cours'); }}
                                            className="cursor-pointer hover:bg-slate-200">{u.metrics.active_dossiers} dossiers actifs</Badge>
                                        {(u.teams || []).slice(0, 1).map(t => (
                                            <Badge key={t.id} variant="outline" className="text-slate-500">{t.name}</Badge>
                                        ))}
                                        {u.metrics.taches_en_retard > 0 && (
                                            <Badge role="button" data-testid={`score-retard-${u.user_id}`}
                                                onClick={(e) => { e.stopPropagation(); goTasks(u.user_id, 'overdue'); }}
                                                className="bg-amber-100 text-amber-700 cursor-pointer hover:bg-amber-200">{u.metrics.taches_en_retard} tâches en retard</Badge>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="user-detail-dialog">
                    {!detail ? (
                        <>
                            <DialogHeader className="sr-only"><DialogTitle>Détail Impact Score</DialogTitle></DialogHeader>
                            <DialogDescription className="sr-only">Chargement du détail de l'Impact Score</DialogDescription>
                            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                        </>
                    ) : (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex flex-wrap items-center gap-2">
                                    <ScoreRing value={detail.global_score} size={48} />
                                    {detail.first_name} {detail.last_name}
                                    <Badge className={labelBadge(detail.global_score)}>{detail.label}</Badge>
                                    <Badge className={riskBadge(detail.risque_decrochage)}>Décrochage : {detail.risque_decrochage}</Badge>
                                </DialogTitle>
                                <DialogDescription className="sr-only">Détail de l'Impact Score de l'utilisateur</DialogDescription>
                            </DialogHeader>

                            {/* 🎯 Bouton Uatu — Prépare mon 1-to-1 (VP + admin, pour les rôles apporteur/parrain) */}
                            {['apporteur', 'parrain'].includes(detail.role) && (
                                <OneToOneBriefButton
                                    apporteurId={selected}
                                    apporteurName={`${detail.first_name} ${detail.last_name}`}
                                />
                            )}

                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                                {subsForRole(detail.role).map(s => <SubScore key={s.key} icon={s.icon} label={s.label} value={detail.scores[s.key]} />)}
                            </div>

                            <div className="mt-3">
                                <ConnInfo metrics={detail.metrics} />
                            </div>

                            {/* Historique */}
                            {history && history.length > 1 && (
                                <div className="mt-4 rounded-lg border p-3">
                                    <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Évolution du score</p>
                                    <ResponsiveContainer width="100%" height={160}>
                                        <LineChart data={history.map(h => ({ date: h.date?.slice(5), score: h.global_score }))}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                                            <XAxis dataKey="date" fontSize={11} stroke="#94a3b8" />
                                            <YAxis domain={[0, 100]} fontSize={11} stroke="#94a3b8" width={28} />
                                            <Tooltip />
                                            <Line type="monotone" dataKey="score" stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Team Impact (VP / managers) */}
                            {detail.team_members && detail.team_members.length > 0 && (
                                <div className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3" data-testid="team-impact-block">
                                    <p className="text-xs font-semibold text-indigo-700 mb-2 flex items-center gap-1">
                                        <UsersRound className="w-3.5 h-3.5" /> Team Impact Score : {detail.team_impact_score ?? 'N/A'} · {detail.team_size} membre(s)
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {detail.team_members.map((mb, i) => (
                                            <Badge key={i} variant="outline" className={`text-[11px] ${scoreColor(mb.global_score)}`}>
                                                {mb.first_name} {mb.last_name} : {mb.global_score ?? '—'}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4 text-sm">
                                <Metric label="Dossiers actifs" value={detail.metrics.active_dossiers}
                                    onClick={() => goProspects(selected, '&view=en_cours')} />
                                <Metric label="Couverture tâches" value={`${detail.metrics.couverture_pct}%`} />
                                <Metric label="Sécurisés" value={`${detail.metrics.securisation_pct}%`} />
                                <Metric label="Non sécurisés" value={detail.metrics.dossiers_non_securises} warn={detail.metrics.dossiers_non_securises > 0}
                                    onClick={detail.metrics.dossiers_non_securises > 0 ? () => goProspectIds((detail.alerts?.non_securises || []).map(d => d.id)) : undefined} />
                                <Metric label="Tâches en retard" value={detail.metrics.taches_en_retard} warn={detail.metrics.taches_en_retard > 0}
                                    onClick={() => goTasks(selected, 'overdue')} />
                                <Metric label="Qualité docs Courtage" value={detail.metrics.qualite_docs_pct === null ? 'N/A' : `${detail.metrics.qualite_docs_pct}%`} />
                                <Metric label="Pipeline frais" value={detail.metrics.pipeline_frais_pct === null ? 'N/A' : `${detail.metrics.pipeline_frais_pct}%`} />
                                <Metric label="Créés (30j)" value={detail.metrics.dossiers_crees_fenetre}
                                    onClick={() => goProspects(selected)} />
                                <Metric label="Tps sans action" value={`${detail.metrics.temps_moyen_sans_action_jours}j`} warn={detail.metrics.temps_moyen_sans_action_jours > 14} />
                                <Metric label="Jours / connexion" value={detail.metrics.jours_depuis_connexion === null ? 'N/A' : `${detail.metrics.jours_depuis_connexion}j`} warn={detail.metrics.jours_depuis_connexion > 7} />
                                <Metric label="Idées (adopt.)" value={`${detail.metrics.idees_proposees} (${detail.metrics.idees_adoptees})`} />
                                <Metric label="Académie modules" value={detail.metrics.academie_modules} />
                                <Metric label="Activité VNX" value={detail.metrics.vnx_activite} />
                            </div>

                            {detail.alerts?.non_securises?.length > 0 && (
                                <div className="mt-3 rounded-lg border border-amber-100 bg-amber-50/50 p-3" data-testid="non-securises-block">
                                    <p className="text-xs font-semibold text-amber-700 flex items-center gap-1 mb-1.5">
                                        <AlertTriangle className="w-3.5 h-3.5" /> Dossiers à relancer (aucune action planifiée) · {detail.alerts.non_securises.length}
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {detail.alerts.non_securises.map(d => (
                                            <Badge key={d.id} variant="outline"
                                                onClick={() => navigate(`/prospects/${d.id}`)}
                                                data-testid={`non-secure-dossier-${d.id}`}
                                                className="text-[11px] text-amber-700 border-amber-200 cursor-pointer hover:bg-amber-100">
                                                {d.name}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mt-4 rounded-xl border bg-gradient-to-br from-violet-50 to-blue-50 p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="font-semibold flex items-center gap-2 text-violet-800">
                                        <Sparkles className="w-4 h-4" /> Analyse IA
                                    </p>
                                    <Button size="sm" variant="outline" onClick={runAi} disabled={aiLoading} data-testid="generate-ai-btn">
                                        {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (detail.ai_analysis ? 'Régénérer' : 'Générer')}
                                    </Button>
                                </div>
                                {!detail.ai_analysis ? (
                                    <p className="text-sm text-slate-500">Cliquez sur « Générer » pour obtenir une synthèse personnalisée.</p>
                                ) : (
                                    <div className="space-y-3 text-sm">
                                        <AiBlock icon={ThumbsUp} color="text-emerald-600" title="Ce qui est bien fait" text={detail.ai_analysis.ce_qui_est_bien} />
                                        <AiBlock icon={ThumbsDown} color="text-orange-600" title="Ce qui est mal fait" text={detail.ai_analysis.ce_qui_est_mal} />
                                        <AiBlock icon={AlertTriangle} color="text-red-600" title="Risques observés" text={detail.ai_analysis.risques} />
                                        {detail.ai_analysis.actions_prioritaires?.length > 0 && (
                                            <div>
                                                <p className="flex items-center gap-1.5 font-medium text-blue-700"><Target className="w-4 h-4" />Actions prioritaires</p>
                                                <ol className="mt-1 ml-5 list-decimal space-y-0.5 text-slate-700">
                                                    {detail.ai_analysis.actions_prioritaires.map((a, i) => <li key={i}>{a}</li>)}
                                                </ol>
                                            </div>
                                        )}
                                        {detail.ai_analysis.message_manager && (
                                            <div className="rounded-lg bg-white/70 p-3 border border-violet-100">
                                                <p className="flex items-center gap-1.5 font-medium text-violet-700 mb-1"><MessageSquare className="w-4 h-4" />Message conseillé au manager</p>
                                                <p className="italic text-slate-700">"{detail.ai_analysis.message_manager}"</p>
                                            </div>
                                        )}
                                        {detail.ai_analysis.generated_at && (
                                            <p className="text-[11px] text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />Généré le {new Date(detail.ai_analysis.generated_at).toLocaleString('fr-FR')}</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}

function RankCard({ title, icon: Icon, color, items, onClick, showProg, empty }) {
    return (
        <Card data-testid={`rank-${title}`}>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-1.5"><Icon className={`w-4 h-4 ${color}`} />{title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
                {items.length === 0 ? (
                    <p className="text-xs text-slate-400">{empty || '—'}</p>
                ) : items.map((u, i) => (
                    <button key={u.user_id} onClick={() => onClick(u.user_id)}
                        className="flex w-full items-center justify-between rounded-md px-2 py-1 text-sm hover:bg-slate-50">
                        <span className="truncate text-slate-700">{i + 1}. {u.first_name} {u.last_name}</span>
                        <span className={`font-semibold ${color}`}>
                            {showProg ? `${u.progression > 0 ? '+' : ''}${u.progression}` : Math.round(u.global_score)}
                        </span>
                    </button>
                ))}
            </CardContent>
        </Card>
    );
}

function Metric({ label, value, warn, onClick }) {
    const clickable = typeof onClick === 'function';
    return (
        <div
            onClick={onClick}
            data-testid={clickable ? `metric-${(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}` : undefined}
            className={`rounded-lg border p-2 transition ${warn ? 'border-amber-200 bg-amber-50/50' : 'bg-slate-50'} ${clickable ? 'cursor-pointer hover:border-blue-300 hover:bg-blue-50/60 hover:shadow-sm' : ''}`}>
            <p className="text-[11px] text-slate-500 flex items-center gap-1">{label}{clickable && <span className="text-blue-400">›</span>}</p>
            <p className={`font-semibold ${warn ? 'text-amber-700' : 'text-slate-800'}`}>{value}</p>
        </div>
    );
}

function AiBlock({ icon: Icon, color, title, text }) {
    if (!text) return null;
    return (
        <div>
            <p className={`flex items-center gap-1.5 font-medium ${color}`}><Icon className="w-4 h-4" />{title}</p>
            <p className="text-slate-700 mt-0.5">{text}</p>
        </div>
    );
}
